import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { navigate } from '../App'
import { colors } from '../theme'
import {
  getSequence,
  listSequenceEnrollments,
  patchEnrollment,
  patchSequence,
  researchContact,
  type Enrollment,
  type EnrollmentStatus,
  type Sequence,
  type SequenceStatus,
} from '../api/sequences'

const ENROLLED_GRID_COLUMNS = '1.6fr 1.4fr 0.6fr 0.8fr 0.9fr 1fr'

export default function SequenceDetail({ id }: { id: string }) {
  const { authHeaders, apiBase } = useAuth()
  const [seq, setSeq] = useState<Sequence | null>(null)
  const [enrollments, setEnrollments] = useState<Enrollment[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<'steps' | 'enrollments'>('steps')

  const reload = useCallback(async () => {
    setError(null)
    try {
      const [s, en] = await Promise.all([
        getSequence(apiBase, authHeaders(), id),
        listSequenceEnrollments(apiBase, authHeaders(), id),
      ])
      setSeq(s)
      setEnrollments(en)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    }
  }, [apiBase, authHeaders, id])

  useEffect(() => {
    void reload()
  }, [reload])

  async function toggleStatus() {
    if (!seq) return
    const next: SequenceStatus = seq.status === 'active' ? 'paused' : 'active'
    try {
      const updated = await patchSequence(apiBase, authHeaders(), id, {
        status: next,
      })
      setSeq(updated)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Status change failed')
    }
  }

  async function patchEnrollmentStatus(eid: string, status: EnrollmentStatus) {
    try {
      await patchEnrollment(apiBase, authHeaders(), eid, { status })
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed')
    }
  }

  if (!seq) {
    return (
      <div style={{ color: colors.textMuted }}>
        {error ? `Error: ${error}` : 'Loading…'}
      </div>
    )
  }

  return (
    <div>
      <button
        onClick={() => navigate({ name: 'sequences' })}
        style={{
          background: 'transparent',
          border: 'none',
          color: colors.purple,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '1.2px',
          textTransform: 'uppercase',
          cursor: 'pointer',
          padding: 0,
          marginBottom: 12,
        }}
      >
        ← All sequences
      </button>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '2.5px',
              color: colors.purple,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Sequence
          </div>
          <h1
            style={{
              fontSize: 30,
              fontWeight: 700,
              letterSpacing: '-1px',
              margin: 0,
              color: colors.black,
            }}
          >
            {seq.name}
          </h1>
          <div
            style={{
              color: colors.textMuted,
              fontSize: 13,
              lineHeight: 1.5,
              marginTop: 6,
              maxWidth: 720,
            }}
          >
            {seq.target_audience || 'No audience description'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={toggleStatus} style={btnGhost}>
            {seq.status === 'active' ? 'Pause' : 'Resume'}
          </button>
          <button
            onClick={() => navigate({ name: 'prospect' })}
            style={btnPrimary}
          >
            + Add prospects
          </button>
        </div>
      </div>

      <StatsRow seq={seq} />

      <div
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: `1px solid ${colors.divider}`,
          marginBottom: 20,
        }}
      >
        <TabButton
          label="Steps"
          active={tab === 'steps'}
          onClick={() => setTab('steps')}
        />
        <TabButton
          label={`Enrollments (${enrollments?.length ?? 0})`}
          active={tab === 'enrollments'}
          onClick={() => setTab('enrollments')}
        />
      </div>

      {tab === 'steps' ? (
        <StepsList steps={seq.steps} />
      ) : (
        <EnrollmentsTable
          rows={enrollments}
          onPatch={patchEnrollmentStatus}
        />
      )}

      {error ? (
        <div
          style={{
            marginTop: 12,
            padding: '10px 14px',
            background: colors.warningBg,
            border: `1.5px solid ${colors.warningBorder}`,
            borderRadius: 8,
            color: colors.red,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      ) : null}
    </div>
  )
}

/** Short display names for the sender registry (app/services/sequences/mailboxes.py). */
const MAILBOX_LABELS: Record<string, string> = {
  rep: 'REP',
  onboarding: 'ONBOARDING@',
  relationships: 'RELATIONSHIPS@',
}

function StatsRow({ seq }: { seq: Sequence }) {
  const items = [
    { label: 'Steps', value: seq.steps?.length ?? 0 },
    { label: 'Enrolled', value: seq.stats?.enrolled_count ?? 0 },
    { label: 'Sent', value: seq.stats?.sent_count ?? 0 },
    { label: 'Replied', value: seq.stats?.replied_count ?? 0 },
    { label: 'Status', value: seq.status.toUpperCase() },
    // Sequences created before multi-mailbox have no `mailbox` key and send
    // as the rep, which is what the fallback reflects.
    { label: 'Sends as', value: MAILBOX_LABELS[seq.mailbox ?? 'rep'] },
  ]
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: 12,
        marginBottom: 24,
      }}
    >
      {items.map((it) => (
        <div
          key={it.label}
          style={{
            background: colors.white,
            border: `1px solid ${colors.divider}`,
            borderRadius: 10,
            padding: '14px 16px',
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '2px',
              color: colors.purple,
              textTransform: 'uppercase',
            }}
          >
            {it.label}
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: colors.black,
              marginTop: 4,
            }}
          >
            {it.value}
          </div>
        </div>
      ))}
    </div>
  )
}

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'transparent',
        border: 'none',
        color: active ? colors.purple : colors.textMuted,
        borderBottom: `3px solid ${active ? colors.purple : 'transparent'}`,
        padding: '12px 16px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '1.2px',
        textTransform: 'uppercase',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )
}

function StepsList({ steps }: { steps: Sequence['steps'] }) {
  if (!steps?.length) {
    return (
      <div
        style={{
          background: colors.white,
          border: `1px dashed ${colors.gray}`,
          borderRadius: 10,
          padding: 30,
          color: colors.textMuted,
          textAlign: 'center',
        }}
      >
        No steps configured.
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {steps.map((s, i) => (
        <div
          key={i}
          style={{
            background: colors.white,
            border: `1px solid ${colors.divider}`,
            borderRadius: 12,
            padding: 18,
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <div
              style={{
                background: colors.purple,
                color: colors.white,
                borderRadius: 999,
                padding: '4px 10px',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
              }}
            >
              Step {i + 1}
            </div>
            <div style={{ fontSize: 12, color: colors.textMuted }}>
              Day {s.day_offset} · {s.channel}
            </div>
          </div>
          {s.subject_template ? (
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: colors.black,
                marginBottom: 8,
              }}
            >
              {s.subject_template}
            </div>
          ) : (
            <div
              style={{
                fontSize: 12,
                color: colors.textMuted,
                fontStyle: 'italic',
                marginBottom: 8,
              }}
            >
              (continues prior thread)
            </div>
          )}
          <div
            style={{
              fontSize: 12,
              color: colors.textMuted,
              whiteSpace: 'pre-wrap',
              lineHeight: 1.55,
              background: colors.bgAlt,
              border: `1px solid ${colors.divider}`,
              borderRadius: 8,
              padding: '10px 12px',
            }}
          >
            <strong style={{ color: colors.purple }}>Body prompt:</strong>{' '}
            {s.body_prompt}
          </div>
          {s.fallback_body ? (
            <details style={{ marginTop: 10 }}>
              <summary
                style={{
                  fontSize: 11,
                  color: colors.textMuted,
                  cursor: 'pointer',
                  letterSpacing: '0.5px',
                }}
              >
                Show fallback copy
              </summary>
              <div
                style={{
                  marginTop: 8,
                  background: colors.bgAlt,
                  border: `1px solid ${colors.divider}`,
                  borderRadius: 8,
                  padding: '10px 12px',
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: colors.black,
                  whiteSpace: 'pre-wrap',
                }}
              >
                <strong>{s.fallback_subject || '(no subject)'}</strong>
                {'\n'}
                {s.fallback_body}
              </div>
            </details>
          ) : null}
        </div>
      ))}
    </div>
  )
}

function EnrollmentsTable({
  rows,
  onPatch,
}: {
  rows: Enrollment[] | null
  onPatch: (id: string, s: EnrollmentStatus) => void
}) {
  const [expanded, setExpanded] = useState<string | null>(null)
  if (rows === null) {
    return <div style={{ color: colors.textMuted }}>Loading enrollments…</div>
  }
  if (rows.length === 0) {
    return (
      <div
        style={{
          background: colors.white,
          border: `1px dashed ${colors.gray}`,
          borderRadius: 10,
          padding: 30,
          color: colors.textMuted,
          textAlign: 'center',
        }}
      >
        No one enrolled yet. Use Find prospects to add some.
      </div>
    )
  }
  return (
    <div
      style={{
        background: colors.white,
        border: `1px solid ${colors.divider}`,
        borderRadius: 12,
        overflowX: 'auto',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: ENROLLED_GRID_COLUMNS,
          minWidth: 760,
          padding: '12px 18px',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '2px',
          color: colors.purple,
          textTransform: 'uppercase',
          background: colors.bgAlt,
          borderBottom: `1px solid ${colors.divider}`,
        }}
      >
        <div>Contact</div>
        <div>Company / Title</div>
        <div>Step</div>
        <div>Status</div>
        <div>Next send</div>
        <div></div>
      </div>
      {rows.map((r, i) => {
        const isOpen = expanded === r.id
        return (
          <div
            key={r.id}
            style={{
              borderBottom:
                i === rows.length - 1 ? 'none' : `1px solid ${colors.divider}`,
            }}
          >
            <div
              onClick={() => setExpanded(isOpen ? null : r.id)}
              style={{
                display: 'grid',
                gridTemplateColumns: ENROLLED_GRID_COLUMNS,
                minWidth: 760,
                padding: '12px 18px',
                alignItems: 'center',
                fontSize: 13,
                color: colors.black,
                cursor: 'pointer',
                background: isOpen ? colors.purpleSoft : colors.white,
              }}
            >
              <div>
                <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    aria-hidden
                    style={{
                      display: 'inline-block',
                      color: colors.purple,
                      transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 120ms',
                      fontSize: 12,
                    }}
                  >
                    ▶
                  </span>
                  {r.contact?.full_name || '—'}
                  {r.contact?.lead_status === 'warm' ? (
                    <span
                      style={{
                        background: '#FFF1D6',
                        color: '#7B4A00',
                        borderRadius: 999,
                        padding: '2px 8px',
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: '1.2px',
                        textTransform: 'uppercase',
                        marginLeft: 4,
                      }}
                      title="Promoted to warm by reply detector"
                    >
                      Warm
                    </span>
                  ) : null}
                </div>
                <div style={{ color: colors.textMuted, fontSize: 12, marginLeft: 18 }}>
                  {r.contact?.email}
                </div>
              </div>
              <div style={{ color: colors.textMuted, fontSize: 12 }}>
                <div style={{ color: colors.black, fontSize: 13 }}>
                  {r.contact?.company}
                </div>
                <div>{r.contact?.title}</div>
              </div>
              <div>{r.current_step}</div>
              <div>
                <EnrollmentStatusPill status={r.status} />
              </div>
              <div style={{ color: colors.textMuted, fontSize: 12 }}>
                {r.next_send_at ? formatDate(r.next_send_at) : '—'}
              </div>
              <div
                style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}
                onClick={(e) => e.stopPropagation()}
              >
                {r.status === 'active' ? (
                  <button onClick={() => onPatch(r.id, 'paused')} style={miniBtn}>
                    Pause
                  </button>
                ) : null}
                {r.status === 'paused' ? (
                  <button onClick={() => onPatch(r.id, 'active')} style={miniBtn}>
                    Resume
                  </button>
                ) : null}
                <button onClick={() => onPatch(r.id, 'replied')} style={miniBtn}>
                  Mark replied
                </button>
                {r.status !== 'stopped' && r.status !== 'completed' ? (
                  <button
                    onClick={() => onPatch(r.id, 'stopped')}
                    style={{
                      ...miniBtn,
                      color: colors.red,
                      borderColor: colors.warningBorder,
                    }}
                  >
                    Stop
                  </button>
                ) : null}
              </div>
            </div>
            {isOpen ? <SendLogPanel enrollment={r} /> : null}
          </div>
        )
      })}
    </div>
  )
}

function SendLogPanel({ enrollment }: { enrollment: Enrollment }) {
  const log = enrollment.send_log || []
  return (
    <div
      style={{
        background: colors.bgAlt,
        borderTop: `1px solid ${colors.divider}`,
        padding: '14px 22px 18px 40px',
      }}
    >
      <ResearchBlock contactId={enrollment.contact_id} />
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '2px',
          color: colors.purple,
          textTransform: 'uppercase',
          marginBottom: 10,
          marginTop: 16,
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <span>Send log</span>
        {enrollment.thread_id ? (
          <a
            href={`https://mail.google.com/mail/u/0/#all/${encodeURIComponent(enrollment.thread_id)}`}
            target="_blank"
            rel="noreferrer"
            style={{
              color: colors.blue,
              textDecoration: 'none',
              fontSize: 10,
              letterSpacing: '1.5px',
            }}
          >
            Open Gmail thread ↗
          </a>
        ) : null}
        {enrollment.reply_detected_at ? (
          <span
            style={{
              color: '#0e7a3a',
              fontSize: 10,
              letterSpacing: '1.5px',
            }}
          >
            Reply detected {formatDate(enrollment.reply_detected_at)}
          </span>
        ) : null}
      </div>
      {log.length === 0 ? (
        <div style={{ color: colors.textMuted, fontSize: 12 }}>
          Nothing sent yet — the dispatcher ticks every 10 minutes and
          will pick this up at the next scheduled send.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {log.map((entry, idx) => (
            <SendLogEntryCard key={idx} entry={entry} />
          ))}
        </div>
      )}
    </div>
  )
}

function SendLogEntryCard({
  entry,
}: {
  entry: Enrollment['send_log'][number]
}) {
  const failed = entry.status === 'failed'
  return (
    <div
      style={{
        background: colors.white,
        border: `1px solid ${
          failed ? colors.warningBorder : colors.divider
        }`,
        borderRadius: 10,
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          flexWrap: 'wrap',
          fontSize: 11,
          marginBottom: 8,
        }}
      >
        <span
          style={{
            background: failed ? colors.warningBg : colors.purpleSoft,
            color: failed ? colors.red : colors.purple,
            borderRadius: 999,
            padding: '3px 8px',
            fontWeight: 700,
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
          }}
        >
          Step {entry.step + 1} · {entry.status}
        </span>
        <span style={{ color: colors.textMuted }}>
          {formatDate(entry.sent_at)}
        </span>
        {entry.provider ? (
          <span style={{ color: colors.textMuted, fontSize: 10 }}>
            {entry.provider}
            {entry.model ? ` · ${entry.model}` : ''}
          </span>
        ) : null}
        {entry.gmail_message_id ? (
          <a
            href={`https://mail.google.com/mail/u/0/#all/${encodeURIComponent(entry.gmail_thread_id || entry.gmail_message_id)}`}
            target="_blank"
            rel="noreferrer"
            style={{
              color: colors.blue,
              textDecoration: 'none',
              fontSize: 10,
              letterSpacing: '1px',
            }}
          >
            Open in Gmail ↗
          </a>
        ) : null}
      </div>
      {entry.subject ? (
        <div
          style={{
            fontWeight: 700,
            fontSize: 13,
            color: colors.black,
            marginBottom: 6,
          }}
        >
          {entry.subject}
        </div>
      ) : null}
      <div
        style={{
          fontSize: 12,
          color: colors.black,
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
        }}
      >
        {entry.body}
      </div>
      {entry.error ? (
        <div
          style={{
            marginTop: 8,
            fontSize: 11,
            color: colors.red,
            background: colors.warningBg,
            border: `1px solid ${colors.warningBorder}`,
            borderRadius: 6,
            padding: '6px 8px',
          }}
        >
          <strong>Error:</strong> {entry.error}
        </div>
      ) : null}
    </div>
  )
}

function EnrollmentStatusPill({ status }: { status: EnrollmentStatus }) {
  const tone =
    status === 'active'
      ? { bg: colors.purpleSoft, fg: colors.purple }
      : status === 'replied'
        ? { bg: '#e6f7ee', fg: '#0e7a3a' }
        : status === 'paused'
          ? { bg: colors.bgAlt, fg: colors.textMuted }
          : status === 'completed'
            ? { bg: colors.bgAlt, fg: colors.black }
            : { bg: colors.warningBg, fg: colors.red }
  return (
    <span
      style={{
        background: tone.bg,
        color: tone.fg,
        borderRadius: 999,
        padding: '3px 9px',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '1.4px',
        textTransform: 'uppercase',
      }}
    >
      {status}
    </span>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}


function ResearchBlock({ contactId }: { contactId: string }) {
  // We fetch the contact lazily on first expand so the table doesn't
  // do N+1 calls. The /contacts endpoint isn't paginated yet — the
  // dispatcher's contact records are O(50 per rep) so a single GET is
  // fine. We just refetch the one we need here.
  const { apiBase, authHeaders } = useAuth()
  const [contact, setContact] = useState<{
    enrichment?: Record<string, unknown>
    full_name?: string
  } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadContact = useCallback(async () => {
    try {
      const r = await fetch(`${apiBase}/contacts/${encodeURIComponent(contactId)}`, {
        headers: authHeaders(),
      })
      if (!r.ok) return
      const body = await r.json()
      setContact(body)
    } catch {
      // best effort
    }
  }, [apiBase, authHeaders, contactId])

  useEffect(() => {
    void loadContact()
  }, [loadContact])

  const enrichment = (contact?.enrichment ?? {}) as Record<string, any>
  const briefing = (enrichment.briefing as string | undefined) ?? ''
  const generatedAt =
    (enrichment.briefing_generated_at as string | undefined) ?? ''
  const sources =
    (enrichment.briefing_sources_loaded as string[] | undefined) ?? []
  const grounded = Boolean(enrichment.briefing_grounded)

  async function runResearch() {
    setBusy(true)
    setError(null)
    try {
      const r = await researchContact(apiBase, authHeaders(), contactId)
      setContact(r.contact)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Research failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      style={{
        background: colors.white,
        border: `1px solid ${colors.divider}`,
        borderRadius: 10,
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: briefing ? 10 : 0,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '2px',
            color: colors.purple,
            textTransform: 'uppercase',
          }}
        >
          Deep research
        </span>
        {briefing ? (
          <span style={{ fontSize: 11, color: colors.textMuted }}>
            generated {formatDate(generatedAt)}
            {sources.length ? ` · sources: ${sources.join(', ')}` : ''}
            {grounded ? ' · grounded' : ''}
          </span>
        ) : (
          <span style={{ fontSize: 12, color: colors.textMuted }}>
            No briefing yet. Pulls Apollo + LinkedIn + company website +
            grounded web search and synthesizes a 1-page sales briefing.
          </span>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <button
            onClick={runResearch}
            disabled={busy}
            style={{
              ...miniBtn,
              background: briefing ? colors.white : colors.purple,
              color: briefing ? colors.purple : colors.white,
              borderColor: colors.purple,
              opacity: busy ? 0.6 : 1,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? 'Researching…' : briefing ? 'Refresh' : 'Run research'}
          </button>
        </div>
      </div>
      {error ? (
        <div
          style={{
            color: colors.red,
            fontSize: 12,
            marginBottom: 8,
          }}
        >
          {error}
        </div>
      ) : null}
      {briefing ? (
        <div
          style={{
            background: colors.bgAlt,
            border: `1px solid ${colors.divider}`,
            borderRadius: 8,
            padding: '10px 12px',
            fontSize: 13,
            color: colors.black,
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
            fontFamily:
              "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            maxHeight: 320,
            overflow: 'auto',
          }}
        >
          {briefing}
        </div>
      ) : null}
    </div>
  )
}

const miniBtn: React.CSSProperties = {
  background: colors.white,
  color: colors.black,
  border: `1px solid ${colors.divider}`,
  borderRadius: 6,
  padding: '4px 8px',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '1px',
  textTransform: 'uppercase',
  cursor: 'pointer',
}
const btnPrimary: React.CSSProperties = {
  background: colors.purple,
  color: colors.white,
  border: 'none',
  borderRadius: 8,
  padding: '8px 16px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  cursor: 'pointer',
}
const btnGhost: React.CSSProperties = {
  background: colors.white,
  color: colors.black,
  border: `1px solid ${colors.divider}`,
  borderRadius: 8,
  padding: '8px 14px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  cursor: 'pointer',
}
